import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Save, Upload, X, Image as ImageIcon, Smartphone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BrandingTab = ({ branding, setBranding, saving, onSave, token }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingMobileLogo, setUploadingMobileLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Invalid file type. Upload PNG, JPG, SVG or WebP.');
      return;
    }

    // Check file size (max 2MB for logo)
    if (file.size > 2 * 1024 * 1024) {
      alert('File too large. Maximum size is 2MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      
      setBranding({ ...branding, logo_url: imageUrl });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const validTypes = ['image/png', 'image/x-icon', 'image/ico', 'image/vnd.microsoft.icon', 'image/svg+xml'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.ico')) {
      alert('Invalid file type. Upload ICO, PNG or SVG.');
      return;
    }

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      
      setBranding({ ...branding, favicon_url: imageUrl });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const removeLogo = () => {
    setBranding({ ...branding, logo_url: '' });
  };

  const removeFavicon = () => {
    setBranding({ ...branding, favicon_url: '' });
  };

  const handleMobileLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Invalid file type. Upload PNG, JPG, SVG or WebP.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('File too large. Maximum size is 2MB.');
      return;
    }

    setUploadingMobileLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url.startsWith('http')
        ? response.data.url
        : `${BACKEND_URL}${response.data.url}`;

      setBranding({ ...branding, mobile_logo_url: imageUrl });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload mobile logo');
    } finally {
      setUploadingMobileLogo(false);
    }
  };

  const removeMobileLogo = () => {
    setBranding({ ...branding, mobile_logo_url: '', mobile_logo_width: null, mobile_logo_height: null });
  };

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Branding Settings</h2>
        <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Logo Upload */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label className="text-zinc-300">Site Logo</Label>
            
            {branding.logo_url ? (
              <div className="relative bg-zinc-800 rounded-lg p-4">
                <img 
                  src={branding.logo_url} 
                  alt="Logo" 
                  className="max-h-20 mx-auto"
                  style={{ 
                    width: branding.logo_width || 'auto',
                    height: branding.logo_height || 'auto',
                    maxWidth: '100%'
                  }}
                />
                <button
                  onClick={removeLogo}
                  className="absolute top-2 right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors bg-zinc-800/50">
                <Upload className="w-8 h-8 text-zinc-500 mb-2" />
                <span className="text-zinc-400 text-sm">Click to upload logo</span>
                <span className="text-zinc-600 text-xs mt-1">PNG, JPG, SVG, WebP (max 2MB)</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
            
            {uploading && (
              <div className="text-center">
                <span className="text-emerald-400 text-sm">Uploading...</span>
              </div>
            )}

            {/* URL Input as fallback */}
            <div className="space-y-2">
              <Label className="text-zinc-500 text-sm">Or enter URL directly:</Label>
              <Input
                value={branding.logo_url || ''}
                onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>

          {/* Favicon Upload */}
          <div className="space-y-3">
            <Label className="text-zinc-300">Favicon</Label>
            
            {branding.favicon_url ? (
              <div className="relative bg-zinc-800 rounded-lg p-4">
                <img 
                  src={branding.favicon_url} 
                  alt="Favicon" 
                  className="w-8 h-8 mx-auto"
                />
                <button
                  onClick={removeFavicon}
                  className="absolute top-2 right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors bg-zinc-800/50">
                <ImageIcon className="w-8 h-8 text-zinc-500 mb-2" />
                <span className="text-zinc-400 text-sm">Click to upload favicon</span>
                <span className="text-zinc-600 text-xs mt-1">ICO, PNG, SVG (32x32 recommended)</span>
                <input
                  type="file"
                  accept="image/x-icon,image/png,image/svg+xml,.ico"
                  onChange={handleFaviconUpload}
                  className="hidden"
                  disabled={uploadingFavicon}
                />
              </label>
            )}
            
            {uploadingFavicon && (
              <div className="text-center">
                <span className="text-emerald-400 text-sm">Uploading...</span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-zinc-500 text-sm">Or enter URL directly:</Label>
              <Input
                value={branding.favicon_url || ''}
                onChange={(e) => setBranding({ ...branding, favicon_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                placeholder="https://example.com/favicon.ico"
              />
            </div>
          </div>
        </div>

        {/* Logo Size Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label className="text-zinc-300">Logo Width (px)</Label>
            <Input
              type="number"
              value={branding.logo_width || 150}
              onChange={(e) => setBranding({ ...branding, logo_width: parseInt(e.target.value) })}
              className="bg-zinc-800 border-zinc-700 text-white"
              min={20}
              max={500}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Logo Height (px)</Label>
            <Input
              type="number"
              value={branding.logo_height || 50}
              onChange={(e) => setBranding({ ...branding, logo_height: parseInt(e.target.value) })}
              className="bg-zinc-800 border-zinc-700 text-white"
              min={20}
              max={200}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Show Site Name</Label>
            <div className="flex items-center gap-2 mt-2">
              <Switch
                checked={branding.show_site_name === true}
                onCheckedChange={(checked) => setBranding({ ...branding, show_site_name: checked })}
              />
              <span className="text-zinc-400 text-sm">{branding.show_site_name === true ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>

        {/* Mobile Logo Settings */}
        <div className="border-t border-zinc-700 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Smartphone className="w-5 h-5 text-emerald-400" />
            <h3 className="text-lg font-medium text-white">Mobile Logo</h3>
            <span className="text-zinc-500 text-sm">(Optional - uses main logo if not set)</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label className="text-zinc-300">Mobile Logo Image</Label>

              {branding.mobile_logo_url ? (
                <div className="relative bg-zinc-800 rounded-lg p-4">
                  <img
                    src={branding.mobile_logo_url}
                    alt="Mobile Logo"
                    className="max-h-16 mx-auto"
                    style={{
                      width: branding.mobile_logo_width || 'auto',
                      height: branding.mobile_logo_height || 'auto',
                      maxWidth: '100%'
                    }}
                  />
                  <button
                    onClick={removeMobileLogo}
                    className="absolute top-2 right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors bg-zinc-800/50">
                  <Smartphone className="w-8 h-8 text-zinc-500 mb-2" />
                  <span className="text-zinc-400 text-sm">Click to upload mobile logo</span>
                  <span className="text-zinc-600 text-xs mt-1">PNG, JPG, SVG, WebP (max 2MB)</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    onChange={handleMobileLogoUpload}
                    className="hidden"
                    disabled={uploadingMobileLogo}
                  />
                </label>
              )}

              {uploadingMobileLogo && (
                <div className="text-center">
                  <span className="text-emerald-400 text-sm">Uploading...</span>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-zinc-500 text-sm">Or enter URL directly:</Label>
                <Input
                  value={branding.mobile_logo_url || ''}
                  onChange={(e) => setBranding({ ...branding, mobile_logo_url: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  placeholder="https://example.com/mobile-logo.png"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Mobile Logo Width (px)</Label>
                <Input
                  type="number"
                  value={branding.mobile_logo_width || ''}
                  onChange={(e) => setBranding({ ...branding, mobile_logo_width: e.target.value ? parseInt(e.target.value) : null })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  min={20}
                  max={300}
                  placeholder="Auto"
                />
                <span className="text-zinc-600 text-xs">Leave empty for auto width</span>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Mobile Logo Height (px)</Label>
                <Input
                  type="number"
                  value={branding.mobile_logo_height || ''}
                  onChange={(e) => setBranding({ ...branding, mobile_logo_height: e.target.value ? parseInt(e.target.value) : null })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  min={20}
                  max={150}
                  placeholder="Auto"
                />
                <span className="text-zinc-600 text-xs">Leave empty for auto height</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingTab;
